import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { populatePrefillData, selectSessionAdvisor } from '@client/model';
import { getStaticPrefillUpdatesForCarrier } from '@shared/policy-application/pre-fill-utils/pre-fill-util';
import { useAdvisorBGA } from './useAdvisorBGA';

interface UseApplyStaticPrefillParams {
  carrier: string;
  clientInfo: Client | null;
  policyFundingSource: PolicyFundingSource | null;
  clientPolicyApplicationId: string;
  formId: string;
  policyType?: FundingPolicyType;
}

export const useApplyStaticPrefill = ({
  carrier,
  clientInfo,
  policyFundingSource,
  clientPolicyApplicationId,
  formId,
  policyType,
}: UseApplyStaticPrefillParams) => {
  const dispatch = useDispatch();

  // Get advisor info with BGA writing code
  const { associatedBGA } = useAdvisorBGA({ policyType });
  const advisor = useSelector(selectSessionAdvisor);
  const advisorInfo = useMemo(
    () =>
      advisor
        ? { ...advisor, bgaWritingCode: associatedBGA?.carrierMeta?.writingCode || '' }
        : null,
    [advisor, associatedBGA],
  );

  const applyStaticPrefill = useCallback(() => {
    if (!clientInfo || !advisorInfo || !policyFundingSource || !formId) {
      return { success: false, updatesCount: 0 };
    }

    // Get all static prefill updates for the carrier
    const updates = getStaticPrefillUpdatesForCarrier(
      carrier,
      advisorInfo,
      policyFundingSource,
    );

    if (updates.length === 0) {
      return { success: false, updatesCount: 0 };
    }

    // Convert updates to PrefillData format
    const prefillData: PrefillData[] = updates.map(update => ({
      questionRef: update.questionRef,
      answer: update.answer,
      type: 'text',
      sourceQuestionRefs: update.sourceQuestionRefs || [],
    }));

    // Dispatch action to populate prefill data in Redux
    dispatch(
      populatePrefillData({
        clientId: clientInfo.clientId,
        clientPolicyApplicationId,
        formId,
        prefillData,
        citations: [], // No citations for static data
        status: 'populated',
        error: null,
      }),
    );

    return { success: true, updatesCount: updates.length };
  }, [
    carrier,
    clientInfo,
    advisorInfo,
    policyFundingSource,
    clientPolicyApplicationId,
    formId,
    dispatch,
  ]);

  return { applyStaticPrefill };
};
